import { Injectable } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, concatMap, finalize, map, toArray } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { Document, emptyDocument } from 'src/app/shared/document';
import { Person } from 'src/app/shared/person';
import { ClientApiService } from './client-api.service';
import { DocumentApiService } from './document-api.service';
import { RestApiService } from './rest-api.service';
import { GetglobalsettingsService } from './getglobalsettings.service';

/** Bank-supplied values that prefill a payment. `bookingDate` is already
 *  parsed (`Date | null`) -- never a raw bank string. */
export interface BankPaymentSeed {
  amountDecimal: number;
  bookingDate: Date | null;
}

/**
 * A payment save that did not fully complete, with what is known about it.
 * `stage: 'header'` -- the header POST failed or its outcome is unknown, so a
 * payment may or may not exist (`documentId` is `null`); `stage: 'items'` --
 * the header was saved as `documentId` but a line item then failed, leaving an
 * item-less payment header. Neither case is retried automatically.
 */
export class PaymentPersistError extends Error {
  constructor(
    public readonly stage: 'header' | 'items',
    public readonly documentId: number | null,
    public readonly originalError: unknown,
  ) {
    super(stage === 'header'
      ? 'Payment header POST failed or its outcome is unknown.'
      : 'Payment header ' + documentId + ' was saved but a line item failed.');
    this.name = 'PaymentPersistError';
  }
}

/**
 * A safety condition failed after the invoice was loaded (stale debt, currency
 * mismatch, unusable booking date, ...). Nothing was created. The auto-create
 * caller falls back to the review dialog; the split dialog marks that one
 * invoice as not created and stops.
 */
export class PaymentPreconditionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PaymentPreconditionError';
  }
}

/** What {@link BankPaymentDocumentService.createPaymentForInvoice} needs. */
export interface CreatePaymentForInvoiceParams {
  invoiceId: number;
  /** Amount to pay against this invoice, in the main currency (UAH) -- the currency `DOCSUM`/`DOCITEMPRICE` are stated in, not the invoice's own `DOCCURRID`. */
  amount: number;
  /** The bank transaction's currency code, compared with the main currency's code -- never with the invoice's own `DOCCURRID`. */
  bankCurrencyCode: string | null | undefined;
  /** The transaction's raw `booking_date` (a string at runtime, see this app's date rule). */
  bookingDate: string | Date | null | undefined;
}

/** Open-debt tolerance, same as the review panel's `invoiceAlreadyPaid()`. */
const DEBT_TOLERANCE = 0.01;

/**
 * The one place a `DOCTYPE=9` child payment of an invoice is *constructed*,
 * shared by `CreateNewDocumentComponent` (the review dialog) and the bank
 * review panel's one-click auto-create, so the two cannot drift on stored
 * values. See `docs/PROMPT_agent-bank-payment-review-auto-create.md`.
 *
 * Construction only -- nothing here writes to the API. The dialog builds on
 * open, before the manager presses Save, so it must never persist from here;
 * persistence stays with the dialog's own `onCreateDocument()` and, for the
 * auto path only, with {@link BankPaymentDocumentService.persistPayment}.
 */
@Injectable({
  providedIn: 'root'
})
export class BankPaymentDocumentService {

  constructor(
    private cliApi: ClientApiService,
    private docApi: DocumentApiService,
    private restApi: RestApiService,
    private globalsettingsservice: GetglobalsettingsService,
  ) {
  }

  /**
   * Builds the unsaved payment for `parent` (a loaded invoice): header fields
   * copied from the invoice, one `PRODID=0` payment line for the seeded bank
   * amount. Synchronous and side-effect free so a caller can publish the
   * result immediately and let {@link fillInChildDocumentDepartmentAndPerson}
   * mutate it afterwards (the dialog's inputs rely on that ordering).
   *
   * @param securityGroupId - `DOCSECGROUPID` of `DocTypes` row 9.
   */
  buildChildPaymentDocument(parent: Document, seed: BankPaymentSeed | null, securityGroupId: number): Document {
    const dddd = new Date();
    const gs = this.globalsettingsservice.getGlobalSettings();
    const amount = seed?.amountDecimal ?? 0;
    // Same normalization the payment editor applies when a manager types an
    // amount (new-document-dlg-process-payment `doUpdateDocItemRow()`), and the
    // app-wide `DOCITEMVPRICE = DOCITEMPRICE / rate` convention. The bank-seeded
    // line used to store the raw amount here, which differs whenever the
    // invoice's DOCRATEVAL is not 1. A missing/zero rate falls back to the raw
    // amount rather than storing Infinity/NaN.
    const rate = parent.DOCRATEVAL;
    const valuePrice = (rate && rate > 0) ? amount / rate : amount;

    return {
      ...emptyDocument,
      ...{
        DOCID: -2,
        DOCACTIVE: 1,
        DOCTYPE: 9,
        DOCCURRID: parent.DOCCURRID,
        DOCRATEVAL: parent.DOCRATEVAL,
        DOCCLIENTSID: parent.DOCCLIENTSID,
        DOCPERSONID: parent.DOCPERSONID,
        DOCCLIDEPID: parent.DOCCLIDEPID,

        DOCPCOCLIID: parent.DOCPCOCLIID,
        DOCPCODEPID: parent.DOCPCODEPID,
        DOCPCOPERID: parent.DOCPCOPERID,

        DOCMANCLIID: this.globalsettingsservice.getMainCompanyId(),
        DOCMANPERID: gs.mainPersonId,
        DOCMANDEPID: gs.paidDepartmentId,

        DOCLCOCLIID: parent.DOCLCOCLIID,

        DOCNOTE1: parent.DOCNOTE1,
        DOCNOTE2: parent.DOCNOTE2,
        PRIMDOCID: parent.DOCID,
        DOCFLAG: 0,
        // Use the bank's own booking date when a seed provides one; otherwise
        // today's date, same as every other payment created through the dialog.
        DOCDATE: seed?.bookingDate ?? dddd,
        DOCVALDATE: seed?.bookingDate ?? dddd,
        DOCEXPDATE: seed?.bookingDate ?? dddd,

        items: [{

          DOCITEMID: -2,
          DOCID: -2,
          DOCTYPE: 9,
          PRODID: 0,
          PRODCODE: 'XXXXXXXXXXXX',
          DOCITEMPRODNAME: 'Оплата',
          DOCITEMPRICE: amount,
          DOCITEMQTY: 1,
          DOCITEMVPRICE: valuePrice,
          DOCITEMSUM: amount,
          DOCITEMCURRID: parent.DOCCURRID,
          DOCITEMCRATE: parent.DOCRATEVAL,
          PAYITEMCLIID: null,
          PAYITEMDEPID: null,
          DOCITEMTEXT1: '',
          DOCITEMTEXT2: 'Нет',
          CURCODE: '',
          U4ETKA: null,

          cloneIndex: 0
        }],

        SGMGROUPID: securityGroupId,
      }
    };
  }

  /**
   * Fills in the local-company and partner-company department/person IDs on
   * `doc` by querying child departments and responsible persons from the API.
   * Mutates and emits `doc`. Fallback: if no child department exists, the
   * parent department ID is used (known limitation).
   *
   * Moved here unchanged from `CreateNewDocumentComponent`, which now
   * delegates to it, so the auto path and the dialog share one implementation.
   * (`new-document-dlg-process-tran-sale` still carries its own separate copy.)
   */
  fillInChildDocumentDepartmentAndPerson(doc: Document): Observable<Document> {
    environment.production || console.log('*** BankPaymentDocumentService before fillInChildDocumentDepartmentAndPerson: ' + JSON.stringify(doc));
    return of(doc).pipe(

      // Our side effect - fill in department and person for local company
      concatMap(ndi => this.cliApi.getChildDepartmentsById(ndi.DOCMANDEPID, ndi.DOCLCOCLIID).pipe(map(d => {
        if (d && (d.length > 0)) {
          ndi.DOCLCODEPID = d[0].CLIID;
        } else {
          ndi.DOCLCODEPID = ndi.DOCMANDEPID; // If no child department, use parent one. Wrong idea but what else?
          environment.production
            || console.log('*** BankPaymentDocumentService in fillInChildDocumentDepartmentAndPerson (no child department): ndi.DOCLCOCLIID: '
                + ndi.DOCLCOCLIID + ', ndi.DOCMANDEPID: ' + ndi.DOCMANDEPID + ', dep: ' + JSON.stringify(d));
        }
        return ndi;
      }))),

      // Their side effect - fill in department and person for partner company
      concatMap(ndi => this.cliApi.getChildDepartmentsById(ndi.DOCCLIDEPID, ndi.DOCPCOCLIID).pipe(map(d => {
        if (d && (d.length > 0)) {
          ndi.DOCPCODEPID = d[0].CLIID;
        } else {
          ndi.DOCPCODEPID = ndi.DOCCLIDEPID; // If no child department, use parent one. Wrong idea but what else?
          environment.production
            || console.log('*** BankPaymentDocumentService in fillInChildDocumentDepartmentAndPerson (no child department): ndi.DOCPCOCLIID: '
                + ndi.DOCPCOCLIID + ', ndi.DOCCLIDEPID: ' + ndi.DOCCLIDEPID + ', dep: ' + JSON.stringify(d));
        }
        return ndi;
      }))),

      // Our side effect - fill in person for local company
      concatMap(ndi => this.cliApi.getPersonsByClientId(ndi.DOCLCOCLIID).pipe(map(prs => {
        const dir: Person = prs.sort((a, b) => (b.PERSRESPONSIBLE - a.PERSRESPONSIBLE)).find(pr => pr.PERSRESPONSIBLE > 0);
        if (dir) {
          ndi.DOCLCOPERID = dir.PERSID;
        } else
          if (prs.length > 0) {
            ndi.DOCLCOPERID = prs[0].PERSID;
          } else {
            ndi.DOCLCOPERID = ndi.DOCMANPERID; // If no person found, use document one. Wrong idea but what else?
            environment.production || console.log('*** BankPaymentDocumentService in fillInChildDocumentDepartmentAndPerson (no person): ' + JSON.stringify(prs));
          }
        return ndi;
      }))),

      // Their side effect - fill in person for partner company
      concatMap(ndi => this.cliApi.getPersonsByClientId(ndi.DOCPCOCLIID).pipe(
        map(prs => {
          const dir: Person = prs.sort((a, b) => (b.PERSRESPONSIBLE - a.PERSRESPONSIBLE)).find(pr => pr.PERSRESPONSIBLE > 0);
          if (dir) {
            ndi.DOCPCOPERID = dir.PERSID;
          } else
            if (prs.length > 0) {
              ndi.DOCPCOPERID = prs[0].PERSID;
            } else {
              ndi.DOCPCOPERID = ndi.DOCPERSONID; // If no person found, use document one. Wrong idea but what else?
              environment.production || console.log('*** BankPaymentDocumentService in fillInChildDocumentDepartmentAndPerson (no person): ' + JSON.stringify(prs));
            }
          return ndi;
        }))),

      finalize(() => {
        environment.production || console.log('*** BankPaymentDocumentService after fillInChildDocumentDepartmentAndPerson: ' + JSON.stringify(doc));
      })
    );
  }

  /**
   * **Auto path only -- the review dialog must never call this** (it builds on
   * open, before the manager confirms with Save). POSTs the payment header,
   * then each line item, sequentially and **without retry** (creation is not
   * idempotent, see `DocumentApiService.postDocumentByIdOnce`). Emits the saved
   * `DOCID` once header and every item have persisted; otherwise errors with a
   * {@link PaymentPersistError} saying what is and is not known to exist.
   */
  persistPayment(doc: Document): Observable<number> {
    return this.docApi.postDocumentByIdOnce(doc).pipe(
      catchError(err => throwError(() => new PaymentPersistError('header', null, err))),
      concatMap(res => {
        const documentId = res?.DOCID;
        if (!documentId || documentId <= 0) {
          return throwError(() => new PaymentPersistError('header', null, res));
        }
        return from(doc.items).pipe(
          concatMap(di => this.docApi.postDocItemByIdOnce({ ...di, DOCID: documentId, cloneIndex: 0 })),
          toArray(),
          catchError(err => throwError(() => new PaymentPersistError('items', documentId, err))),
          map(() => documentId)
        );
      })
    );
  }

  /**
   * Code (upper-cased, trimmed) of the base currency that `DOCSUM`, `PAYDSUM` and
   * `DOCITEMPRICE` are stated in: the profile's main currency. `null` when it
   * cannot be resolved (settings not loaded, unknown id), which callers treat as
   * "do not proceed" -- never as a match.
   */
  getBaseCurrencyCode(): Observable<string | null> {
    const id = this.globalsettingsservice.getMainCurrencyId();
    if (!id) {
      return of(null);
    }
    return this.restApi.getCurrencyInfoByID(id).pipe(
      map(currencies => {
        const code = currencies && currencies[0] ? (currencies[0].CURCODE || '').trim().toUpperCase() : '';
        return code || null;
      })
    );
  }

  /**
   * **Auto path only** (the review dialog never calls this). Creates one
   * payment of `amount` against `invoiceId`: loads the real invoice,
   * re-checks that it is still an active invoice and that `amount` fits its
   * fresh debt, checks the transaction is in the main currency, builds the
   * same payment the review dialog would, and saves it once without retry.
   * Emits the saved payment's `DOCID`.
   *
   * Errors: {@link PaymentPreconditionError} when a safety condition fails
   * (nothing was created); {@link PaymentPersistError} when the save did not
   * fully complete (see that class for what is and is not known to exist).
   */
  createPaymentForInvoice(p: CreatePaymentForInvoiceParams): Observable<number> {
    const bookingDateParsed = p.bookingDate ? new Date(p.bookingDate) : null;
    if (!bookingDateParsed || isNaN(bookingDateParsed.getTime())) {
      return throwError(() => new PaymentPreconditionError('Не визначено дату платежу.'));
    }

    return this.docApi.getDocumentById(p.invoiceId).pipe(
      concatMap(docs => {
        const parent = docs && docs[0];
        if (!parent) {
          return throwError(() => new PaymentPreconditionError('Не вдалося завантажити рахунок.'));
        }
        // Fresh, not the list's: the target may have been voided since the dialog loaded, and
        // `PATCH /:id/process` accepts only an active DOCTYPE=1 -- a payment created against
        // anything else could never be recorded.
        if (parent.DOCTYPE !== 1 || !parent.DOCACTIVE) {
          return throwError(() => new PaymentPreconditionError('Це не активний рахунок.'));
        }
        // Fresh figures, not a list's possibly stale ones: a colleague may
        // have paid this invoice since the queue was loaded.
        const debt = (parent.DOCSUM ?? 0) - (parent.DOCPAIDSUM ?? 0);
        if (parent.DOCSUM == null || parent.DOCPAIDSUM == null || p.amount > debt + DEBT_TOLERANCE) {
          return throwError(() => new PaymentPreconditionError('Сума перевищує актуальний борг за рахунком.'));
        }
        return this.getBaseCurrencyCode().pipe(map(baseCurrency => ({ parent, baseCurrency })));
      }),
      concatMap(({ parent, baseCurrency }) => {
        // `DOCSUM`/`DOCITEMPRICE` are stated in the base (main) currency, whatever the invoice's own
        // `DOCCURRID` is -- that one (e.g. "CUSD") is only a display/rate denomination. So the bank
        // amount is compared with the base currency, never with the invoice's currency code.
        const bankCurrency = (p.bankCurrencyCode || '').trim().toUpperCase();
        if (!baseCurrency) {
          return throwError(() => new PaymentPreconditionError('Не вдалося визначити валюту обліку.'));
        }
        if (!bankCurrency || bankCurrency !== baseCurrency) {
          return throwError(() => new PaymentPreconditionError(
            'Валюта транзакції (' + (p.bankCurrencyCode || '?') + ') відрізняється від валюти обліку (' + baseCurrency + ').'));
        }
        return this.docApi.getDocTypes().pipe(
          map(types => ({ parent, securityGroupId: (types || []).find(t => t.DOCTYPEID == 9)?.DOCSECGROUPID }))
        );
      }),
      concatMap(({ parent, securityGroupId }) => {
        if (securityGroupId == null) {
          return throwError(() => new PaymentPreconditionError('Не вдалося визначити групу доступу для платежу.'));
        }
        const payment = this.buildChildPaymentDocument(
          parent,
          { amountDecimal: p.amount, bookingDate: this.bookingDateForPayment(bookingDateParsed) },
          securityGroupId
        );
        return this.fillInChildDocumentDepartmentAndPerson(payment);
      }),
      concatMap(payment => this.persistPayment(payment))
    );
  }

  /**
   * Ukreximbank's booking_date only ever carries a date, no real time --
   * webyto-agent's tryParseBookingDate() defaults hh/min/ss to 00 whenever
   * the bank's raw string has none (Ukreximbank's own format is date-only,
   * "DD.MM.YYYY"; PrivatBank's always includes a real time). Taking that
   * midnight value verbatim as the created payment's DOCDATE reads as wrong
   * to a manager looking at the saved document later ("14.09.2026 00:00").
   * When the booking date's time is exactly UTC midnight, stamp the current
   * wall-clock time onto its calendar date instead -- a real bank
   * transaction timestamped exactly at midnight is effectively never seen
   * in practice, so this is a safe heuristic rather than a bank-name check,
   * and it stays correct if a future bank source has the same date-only gap.
   * The date's own UTC calendar fields are used (not local getters) because
   * that is how tryParseBookingDate() encoded it (Date.UTC(yyyy, mm-1, dd)),
   * so reading it back with local getters could shift the day for a
   * manager in a timezone behind UTC.
   *
   * A booking date WITH a real time is a bank wall-clock value encoded as
   * UTC by the same parser (18:39 Kyiv -> 18:39Z, offset not applied), so it
   * must be rebuilt from its UTC fields as a LOCAL date too. Returning it
   * verbatim made the saved DOCDATE 3 hours ahead of the bank's time; the
   * payment then post-dated "now", and every "as of today" calculation
   * (e.g. `udf_docsoldparts`'s `DOCDATE <= @todate` payment check) ignored
   * it until the clock caught up (2026-10-06, payment DOCID 128499).
   * The result is also never later than the current time: a bank payment
   * has already happened.
   */
  bookingDateForPayment(bookingDate: string | Date): Date {
    const parsed = new Date(bookingDate);
    const now = new Date();
    const isDateOnly = parsed.getUTCHours() === 0 && parsed.getUTCMinutes() === 0 && parsed.getUTCSeconds() === 0;
    const local = isDateOnly
      ? new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate(),
          now.getHours(), now.getMinutes(), now.getSeconds())
      : new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate(),
          parsed.getUTCHours(), parsed.getUTCMinutes(), parsed.getUTCSeconds());
    return local.getTime() > now.getTime() ? now : local;
  }
}
